import { createContext, createElement, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import type { ChatMessage } from '../lib/chatApi'

export interface ChatState {
  messages: ChatMessage[]
  sessionId: number | null
}

type MessageUpdater = ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])

export interface ChatContextValue {
  state: ChatState
  setMessages: (updater: MessageUpdater) => void
  setSessionId: (id: number | null) => void
}

const STORAGE_KEY = 'cc-chat-state'

export function loadChatState(): ChatState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed.messages)) {
        return { messages: parsed.messages, sessionId: parsed.sessionId ?? null }
      }
    }
  } catch {
    // ignore corrupt data
  }
  return { messages: [], sessionId: null }
}

export function saveChatState(state: ChatState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // sessionStorage full or unavailable
  }
}

export const ChatContext = createContext<ChatContextValue | null>(null)

export function ChatStateProvider({ children }: { children: ReactNode }) {
  const chatStateRef = useRef(loadChatState())
  const [chatMessages, setChatMessages] = useState(chatStateRef.current.messages)
  const [chatSessionId, setChatSessionId] = useState(chatStateRef.current.sessionId)

  const setMessages = useCallback((updater: MessageUpdater) => {
    setChatMessages(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      const state = { messages: next, sessionId: chatStateRef.current.sessionId }
      chatStateRef.current = state
      saveChatState(state)
      return next
    })
  }, [])

  const setSessionId = useCallback((id: number | null) => {
    setChatSessionId(id)
    chatStateRef.current = { ...chatStateRef.current, sessionId: id }
    saveChatState(chatStateRef.current)
  }, [])

  const value = useMemo(() => ({
    state: { messages: chatMessages, sessionId: chatSessionId },
    setMessages,
    setSessionId,
  }), [chatMessages, chatSessionId, setMessages, setSessionId])

  return createElement(ChatContext.Provider, { value }, children)
}

export function useChatState(): ChatContextValue {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error('useChatState must be used within ChatContext.Provider')
  return ctx
}
